import { Location } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Observer, ReplaySubject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { KeyPairs, ReponseActionApi } from '../../core/shared/core.models';
import { SecuriteConfigService } from './securite-config.service';
import { ReponseActionApiAuth, UtilisateurIdz } from './securite.models';
import { ZzzConfigService } from '../../core/config/config.service';

@Injectable({
  providedIn: 'root'
})
export class ZzzSecuriteService {
  private utilisateurCourantSubject = new ReplaySubject<UtilisateurIdz>();
  utilisateurCourant: UtilisateurIdz = undefined;

  #configService = inject(ZzzConfigService);

  #isCallback = false;
  get isCallback(): boolean {
    return this.#isCallback;
  }

  private http: HttpClient;

  public get utilisateurEstAuthentifie(): boolean {
    return this.utilisateurCourant !== undefined;
  }

  get roles(): string[] {
      return <string[]>this.utilisateurCourant.claims.find(c => c.key === 'role')?.value;
  }

  constructor(
    backend: HttpBackend,
    private securiteConfigService: SecuriteConfigService,
    private location: Location,
    private router: Router
    // private traductionService: TraductionService
  ) {
    this.http = new HttpClient(backend);
  }

  hasRole(role: string): boolean {
    return this.roles.findIndex(userRole => userRole == role) >= 0;
  }

  doesNotHaveRole(role: string): boolean {
    return this.roles.findIndex(userRole => userRole == role) == -1;
  }

  init(): Observable<any> {
    return this.majUtilisateurCourant();
  }

  signIn(rep: ReponseActionApiAuth) {
    if (rep.estAuthentifie === true) {
      this.utilisateurCourant = new UtilisateurIdz();
      this.utilisateurCourant.claims = [];
      this.utilisateurCourant.nom = rep.nom;

      var jwtClaim = rep.claims.find((c: KeyPairs) => {
        return c.key.toLowerCase() === 'jwt';
      });

      if (jwtClaim) {
        var jwtHelper = new JwtHelperService();
        var jwtDecoded = jwtHelper.decodeToken(jwtClaim.value);

        for (const p in jwtDecoded) {
          if (
            p.toLowerCase().endsWith('/identity/claims/role') &&
            jwtDecoded[p].indexOf(',') > -1
          ) {
            const roles = jwtDecoded[p].split(',');
            roles.forEach((r) => {
              this.utilisateurCourant.claims.push({
                key: p,
                value: r
              });
            });
            continue;
          }

          this.utilisateurCourant.claims.push({
            key: p,
            value: jwtDecoded[p]
          });
        }
      } else {
        this.utilisateurCourant.claims = rep.claims;
      }
    } else {
      this.utilisateurCourant = undefined;
    }

    this.utilisateurCourantSubject.next(this.utilisateurCourant);
    this.utilisateurCourantSubject.complete();
  }

  onMajUtilisateurCourant(): Observable<UtilisateurIdz> {
    return this.utilisateurCourantSubject;
  }

  redirectToProvider(cb?: string): Observable<any> {
    return new Observable((obs: Observer<any>) => {
      this.getUrlRedirectIdProvider(this.getUrlCallback(cb)).subscribe(
        (rep) => {
          window.location.href = rep.url;
          obs.next(true);
          obs.complete();
        },
        (err) => {
          obs.error(err);
          obs.complete();
        }
      );
    });
  }

  majUtilisateurCourant(): Observable<any> {
    if (!this.securiteConfigService.authApiPresent) return of(null);

    const url = new URL(window.location.href);

    if (url.pathname.endsWith('auth/callback')) {
      this.#isCallback = true;
      return this.traiterAuthCallback(url);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const sso = urlParams.get('sso');
    if (sso == 'true' && !this.utilisateurEstAuthentifie) {
      const cb = urlParams.get('cb');
      return this.redirectToProvider(cb);
    }

    return this.http
      .get<ReponseActionApiAuth>(
        this.securiteConfigService.authApiEndPoint + '/auth/etat',
        { withCredentials: true }
      )
      .pipe(
        map((rep) => {
          this.signIn(rep);
          return rep;
        })
      );
  }

  private traiterAuthCallback(url: URL): Observable<any> {
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    let cb = url.searchParams.get('cb');

    return this.callback(code, state, cb).pipe(
      tap((rep) => {
        this.signIn(rep);
        if (rep.estAuthentifie) {
          if (cb === undefined || cb == null || cb === '') {
            cb = '/';
          }

          var baseUrl = this.securiteConfigService.baseHref || '';
          var loc = `${window.location.protocol}//${window.location.host}${baseUrl}${cb}`;
          window.location.href = loc;

          // Dans ZZZ on utilise le router mais IGO2 ne charge pas, donc utilisation du window.location.href qui règle le problème.
          // this.router.navigateByUrl(cb);
        }

        this.router.navigateByUrl('/');
      })
    );
  }

  connecter(
    utilisateur: string,
    motDePasse: string,
    type: string
  ): Observable<ReponseActionApiAuth> {
    return this.http.post<ReponseActionApiAuth>(
      this.securiteConfigService.authApiEndPoint + '/auth/connecter',
      { utilisateur, motDePasse, type },
      { withCredentials: true }
    );
  }

  callback(
    code: string,
    state: string,
    cb: string
  ): Observable<ReponseActionApiAuth> {
    return this.http.post<ReponseActionApiAuth>(
      this.securiteConfigService.authApiEndPoint + '/auth/callback',
      { code, state, cb },
      { withCredentials: true }
    );
  }

  getUrlCallback(cb?: string): string {
    let loc;

    let baseUrl = this.securiteConfigService.baseHref || '';

    if (this.securiteConfigService.useHash) {
      if (!this.securiteConfigService.baseHref) {
        baseUrl = '/';
      }

      baseUrl = `${baseUrl}#`;
    }

    if (!cb) {
      cb = this.location.path();
      cb = cb.replace('sso=true', '');
    }

    // Définir les bon URL selon la langue en cour
    // if (this.traductionService.getLangueCourante() == 'fr') {
    loc = `${window.location.protocol}//${window.location.host}${baseUrl}/auth/callback?cb=${cb}`;
    // } else {
    //   //cb = "/" + this.traductionService.getLangueCourante() + this.location.path();
    //   loc = `${window.location.protocol}//${window.location.host}${baseUrl}/en/auth/callback?cb=${cb}`;
    // }

    return loc;
  }

  getUrlRoot(): string {
    //const cb = this.location.path();
    let baseUrl = this.securiteConfigService.baseHref || '';

    if (this.securiteConfigService.useHash) {
      if (!this.securiteConfigService.baseHref) {
        baseUrl = '/';
      }

      baseUrl = `${baseUrl}#`;
    }
    let loc: string = `${window.location.protocol}//${window.location.host}${baseUrl}`;
    // if (this.traductionService.getLangueCourante() != 'fr') {
    //   loc = `${window.location.protocol}//${window.location.host}${baseUrl}/${this.traductionService.getLangueCourante()}`;
    // }

    return loc;
  }

  getUrlRedirectIdProvider(
    redirection: string
  ): Observable<ReponseActionApiAuth> {
    return this.http.get<ReponseActionApiAuth>(
      this.securiteConfigService.authApiEndPoint + '/auth/GetUrlRedirect',
      { params: { redirection }, withCredentials: true }
    );
  }

  getLogOutUrl(): Observable<any> {
    return this.http.get<any>(
      this.securiteConfigService.authApiEndPoint + '/auth/logOutUrl',
      { withCredentials: true }
    );
  }

  deconnecter(cb?: string): Observable<any> {
    return new Observable((observer) => {
      this.deconnecterApp(cb).subscribe((rep) => {
        window.location.href = rep.url;
      });
    });
  }

  reconnecter(cb?: string): Observable<any> {
    return new Observable((observer) => {
      this.deconnecterApp().subscribe((rep) => {
        this.redirectToProvider(cb).subscribe();
      });
    });
  }

  private deconnecterApp(cb?: string): Observable<any> {
    if (!cb) {
      cb = this.getUrlRoot();
    }

    return this.http.post<ReponseActionApiAuth>(
      this.securiteConfigService.authApiEndPoint + '/auth/Deconnecter',
      { Cb: cb },
      { withCredentials: true }
    );
  }

  getClaims(): Observable<ReponseActionApiAuth> {
    return this.http.get<ReponseActionApiAuth>(
      this.securiteConfigService.authApiEndPoint + '/auth/GetClaims',
      { withCredentials: true }
    );
  }

  getTypes(): Observable<ReponseActionApi<KeyPairs[]>> {
    return this.http.get<ReponseActionApiAuth>(
      this.securiteConfigService.authApiEndPoint + '/auth/GetTypes',
      { withCredentials: true }
    );
  }

  verifierRoleUtilisateurCourant(role: string): boolean {
    if (!this.utilisateurCourant) {
      return false;
    }

    return (
      this.utilisateurCourant.claims.find((c: KeyPairs) => {
        return (
          c.key.toLowerCase().endsWith('/identity/claims/role') &&
          c.value === role
        );
      }) !== undefined
    );
  }
}
